/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */
// tslint:disable:no-non-null-assertion

import { Injectable } from '@angular/core';
import { BuilderUserContext } from '@gh/builder-shared';
import {
  chartLegendCellBar,
  chartLegendCellLineWithDots,
  chartLegendRow,
  ChartSpecsWithLegend,
} from '@gh/builder-shared-charts';
import {
  DashboardAverageItem,
  DashboardChartType,
  DashboardsRestService,
  DashboardWidgetsCode,
  DashboardWidgetsType,
  Language,
  OfficeRestService,
  OfficeType,
  PriorityAndStageValue,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { DictionaryService, InitViewService } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { MessageBasket } from '@gh/core-messages';
import { TranslationService } from '@gh/core-mls';
import { NotificationService } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { combineLatest$, EMPTY$ } from '@gh/rx';
import { catchError$, map$, switchMap$ } from '@gh/rx/operators';
import { Observable, ObservableInput } from 'rxjs';
import {
  averageBuildTimeFromDto,
  AverageBuildTimeItem,
  averageContractPriceFromDto,
  averageHouseCostChangeDtoFromDto,
  averagePreconstructionBuildTimesFromDto,
  averageTimeFromSfToDepositFromDto,
  conversionRatesFromDto,
  ConversionRatesItem,
  createWidgetDataLoader,
  grossAndNetProfitFromDto,
  grossProfitAndOverheadsFromDto,
  MaintenanceTasksOverdue,
  monthlySalesSlabsFromDto, Office,
  preconstructionBuildTimesFromDto,
  PreconstructionBuildTimesListItem,
  PriorityAndStage,
  priorityAndStageFromDto,
  PriorityAndStageItem,
  salesLeadsBudgetsSpecsFromDto,
  salespersonTotalFromDto,
  SalespersonTotalItem,
  TopCustomersByEarliestContact,
  topCustomersByEarliestContactFromDto,
  TopCustomersByPriority,
  WidgetDataLoader,
  WidgetsModel,
} from './models';

export interface DashboardManageDialogConfig {
  widgets: WidgetsModel[];
  localeIndex: number;
}

@Injectable()
export class DashboardBusinessService implements InitViewService {
  gridTooltips: { [index: string]: string } = {};

  constructor(
    private dictionaryService: DictionaryService,
    private gridLayoutService: GridLayoutService,
    private userContext: BuilderUserContext,
    private officeRest: OfficeRestService,
    private dashboard: DashboardsRestService,
    private translations: TranslationService,
    private notificationService: NotificationService,
  ) {
  }

  initView(): any {
    let initData: any = {
      dashboardChartTypeDict: this.dictionaryService.getByEnum(DashboardChartType),
    };

    if (this.userContext.officeView
      && (this.userContext.hasSystemRole(SystemRoleBit.Franchisee) || this.userContext.isSwitched())) {
      initData = { ...initData, widgets: this.getWidgets(DashboardWidgetsType.FRANCHISEE) };
    } else if (this.userContext.hasSystemRole(SystemRoleBit.SalesPerson)) {
      initData = { ...initData, widgets: this.getWidgets(DashboardWidgetsType.SALESPERSON) };
    } else if (this.userContext.masterView) {
      initData = { ...initData, widgets: this.getWidgets(DashboardWidgetsType.MASTER) };
    } else if (this.userContext.corporateView) {
      initData = { ...initData, widgets: this.getWidgets(DashboardWidgetsType.CORPORATE) };
    } else {
      initData = { ...initData, widgets: [] };
    }

    initData = { ...initData, gridTooltips: this.gridTooltips };
    return initData;
  }

  createCorporateLoaders(): Hash<WidgetDataLoader> {
    return {
      [DashboardWidgetsCode.averageBuildTime]: createWidgetDataLoader(this.getAverageBuildTime()),
      [DashboardWidgetsCode.averagePreconstructionBuildTimes]: createWidgetDataLoader(
        this.getAveragePreconstructionBuildTimes(true)),
      [DashboardWidgetsCode.grossAndNetProfit]: createWidgetDataLoader(this.getGrossAndNetProfit(true)),
      [DashboardWidgetsCode.monthlySalesSlabs]: createWidgetDataLoader(this.getMonthlySalesSlabs()),
      [DashboardWidgetsCode.salesLeadsBudgets]: createWidgetDataLoader(this.getSalesLeadsBudgets()),
      [DashboardWidgetsCode.salespersonTotal]: createWidgetDataLoader(this.getSalespersonTotal()),
    };
  }

  createMasterLoaders(): Hash<WidgetDataLoader> {
    return {
      [DashboardWidgetsCode.grossProfitAndOverheads]: {
        init: () => this.findAllOffices().pipe(
          switchMap$((offices: Office[]) =>
            offices.length
              ? this.getGrossProfitAndOverheads(offices[0].id).pipe(
              map$((data) => <any>({ data, offices })))
              : [{ data: void 0, offices }])),
        update: (_, officeId) => this.getGrossProfitAndOverheads(officeId).pipe(map$((data) => ({ data }))),
      },
      [DashboardWidgetsCode.averageBuildTime]: createWidgetDataLoader(this.getAverageBuildTime()),
      [DashboardWidgetsCode.averagePreconstructionBuildTimes]: createWidgetDataLoader(
        this.getAveragePreconstructionBuildTimes(false)),
      [DashboardWidgetsCode.grossAndNetProfit]: createWidgetDataLoader(this.getGrossAndNetProfit(false)),
      [DashboardWidgetsCode.monthlySalesSlabs]: createWidgetDataLoader(this.getMonthlySalesSlabs()),
      [DashboardWidgetsCode.salesLeadsBudgets]: createWidgetDataLoader(this.getSalesLeadsBudgets()),
    };
  }

  createFranchiseeLoaders(): Hash<WidgetDataLoader> {
    return {
      [DashboardWidgetsCode.averageContractPrice]: createWidgetDataLoader(this.getAverageContractPrice()),
      [DashboardWidgetsCode.averageHouseCostChange]: createWidgetDataLoader(this.getAverageHouseCostChange()),
      [DashboardWidgetsCode.grossProfitAndOverheads]: createWidgetDataLoader(this.getGrossProfitAndOverheads()),
      [DashboardWidgetsCode.maintenanceTasksOverdue]: createWidgetDataLoader(combineLatest$(
        this.getMaintenanceTasksOverdue(),
        this.gridLayoutService.get('maintenanceTasksOverdue')).pipe(
        map$(([data, gridLayout]) => ({ data, gridLayout })))),
      [DashboardWidgetsCode.preconstructionBuildTimes]: createWidgetDataLoader(combineLatest$(
        this.getPreconstructionBuildTimes(),
        this.gridLayoutService.get('preconstructionBuildTimes')).pipe(
        map$(([data, gridLayout]) => ({ data, gridLayout })))),
      [DashboardWidgetsCode.salesLeadsBudgets]: createWidgetDataLoader(this.getSalesLeadsBudgets(true)),
    };
  }

  createSalesLoaders(): Hash<WidgetDataLoader> {
    return {
      [DashboardWidgetsCode.averageTimeFromSfToDeposit]: createWidgetDataLoader(this.getAverageTimeFromSfToDeposit()),
      [DashboardWidgetsCode.conversionRates]: createWidgetDataLoader(combineLatest$(
        this.getConversionRates(),
        this.gridLayoutService.get('conversionRates')).pipe(
        map$(([data, gridLayout]) => ({ data, gridLayout })))),
      [DashboardWidgetsCode.priorityAndStage]: createWidgetDataLoader(this.getPriorityAndStage()),
      [DashboardWidgetsCode.salesLeadsBudgets]: createWidgetDataLoader(this.getSalesLeadsBudgetsForSalesperson()),
      [DashboardWidgetsCode.topCustomersByEarliestContact]: createWidgetDataLoader(combineLatest$(
        this.getTopCustomersByEarliestContact(),
        this.gridLayoutService.get('topCustomersByEarliestContact')).pipe(
        map$(([data, gridLayout]) => ({ data, gridLayout })))),
      [DashboardWidgetsCode.topCustomersByPriority]: createWidgetDataLoader(combineLatest$(
        this.getTopCustomersByPriority(),
        this.gridLayoutService.get('topCustomersByPriority')).pipe(
        map$(([data, gridLayout]) => ({ data, gridLayout })))),
    };
  }

  getAverageBuildTime(): Observable<AverageBuildTimeItem[]> {
    return this.dashboard
      .getAverageBuildTime().pipe(
        map$(averageBuildTimeFromDto));
  }

  getAverageContractPrice(): Observable<DashboardAverageItem[]> {
    return this.dashboard
      .getAverageContractPrice().pipe(
        catchError$(this.catchNoPermissions),
        map$(averageContractPriceFromDto));
  }

  getAverageHouseCostChange(): Observable<DashboardAverageItem[]> {
    return this.dashboard
      .getAverageHouseCostChange().pipe(
        catchError$(this.catchNoPermissions),
        map$(averageHouseCostChangeDtoFromDto));
  }

  getAveragePreconstructionBuildTimes(isCorporate: boolean): Observable<google.visualization.ChartSpecs> {
    return this.dashboard
      .getAveragePreconstructionBuildTimes().pipe(
        map$((data) => averagePreconstructionBuildTimesFromDto(data, isCorporate)),
        map$((specs: google.visualization.ChartSpecs) => {
          specs.options = {
            ...this.getChartDefaultOptions(),
            ...specs.options,
          };

          return specs;
        }));
  }

  getAverageTimeFromSfToDeposit(): Observable<DashboardAverageItem[]> {
    return this.dashboard
      .getAverageTimeFromSfToDeposit().pipe(
        catchError$(this.catchNoPermissions),
        map$(averageTimeFromSfToDepositFromDto));
  }

  getConversionRates(): Observable<ConversionRatesItem[]> {
    return this.dashboard
      .getConversionRates().pipe(
        catchError$(this.catchNoPermissions),
        map$(conversionRatesFromDto));
  }

  getGrossAndNetProfit(isCorporate: boolean): Observable<ChartSpecsWithLegend> {
    return this.dashboard
      .getGrossAndNetProfit().pipe(
        map$((data) => grossAndNetProfitFromDto(data, isCorporate)),
        map$((specs: ChartSpecsWithLegend) => {
          specs.options = {
            ...this.getChartDefaultOptions(),
            ...specs.options,
          };

          return specs;
        }));
  }

  getGrossProfitAndOverheads(officeId?: ID): Observable<google.visualization.ChartSpecs> {
    return this.dashboard
      .getGrossProfitAndOverheads(officeId).pipe(
        catchError$(this.catchNoPermissions),
        map$(grossProfitAndOverheadsFromDto),
        map$((specs: google.visualization.ChartSpecs) => {
          specs.options = {
            ...this.getChartDefaultOptions(),
            ...specs.options,
          };

          return specs;
        }));
  }

  findAllOffices(): Observable<Office[]> {
    const masterZoneId = this.userContext.masterZone!.id;
    return this.officeRest
      .findAllByMasterZone(masterZoneId).pipe(
        map$((offices: Office[]) => offices.filter((office) => office.type === OfficeType.Franchise)));
  }

  getMaintenanceTasksOverdue(): Observable<MaintenanceTasksOverdue[]> {
    return this.dashboard
      .getMaintenanceTasksOverdue().pipe(
        catchError$(this.catchNoPermissions),
        map$((tasks: MaintenanceTasksOverdue[]) => tasks));
  }

  getMonthlySalesSlabs(): Observable<google.visualization.ChartSpecs> {
    return this.dashboard
      .getMonthlySalesSlabs().pipe(
        map$((data: any) => {
          const labels = {
            [DashboardChartType.Sales]: this.translations.get('lbl_current_sales'),
            [DashboardChartType.SalesPrior]: this.translations.get('lbl_prior_sales'),
            [DashboardChartType.Slab]: this.translations.get('lbl_current_slabs'),
            [DashboardChartType.SlabPrior]: this.translations.get('lbl_prior_slabs'),
            [DashboardChartType.SalesBudgets]: this.translations.get('lbl_current_budget'),
          };

          return monthlySalesSlabsFromDto(data, labels);
        }),
        map$((specs: google.visualization.ChartSpecs) => {
          specs.options = {
            ...this.getChartDefaultOptions(),
            ...specs.options,
          };

          return specs;
        }));
  }

  getPreconstructionBuildTimes(): Observable<PreconstructionBuildTimesListItem[]> {
    return this.dashboard
      .getPreconstructionBuildTimes().pipe(
        catchError$(this.catchNoPermissions),
        map$(preconstructionBuildTimesFromDto));
  }

  getPriorityAndStage(): Observable<PriorityAndStageItem[]> {
    return this.dashboard
      .getPriorityAndStage().pipe(
        catchError$(this.catchNoPermissions),
        map$((data: PriorityAndStage) => {
          data.headerValues.forEach((headerValue: string, headerIndex: number) => {
            data.values.forEach((value: PriorityAndStageValue, index: number) => {
              this.addGridTooltip(`${headerValue}${index}`, value.tooltips[headerIndex]);
            });
          });

          return data;
        }),
        map$(priorityAndStageFromDto));
  }

  getSalespersonTotal(): Observable<SalespersonTotalItem[]> {
    return this.dashboard
      .getSalespersonTotal().pipe(
        map$(salespersonTotalFromDto));
  }

  getSalesLeadsBudgets(comboChart: boolean = false): Observable<google.visualization.ChartSpecs> {
    return this.dashboard
      .getSalesLeadsBudgets().pipe(
        catchError$(this.catchNoPermissions),
        map$((data: any) => {
          const labels = {
            [DashboardChartType.Sales]: this.translations.get('lbl_sales'),
            [DashboardChartType.Leads]: this.translations.get('lbl_leads'),
            [DashboardChartType.SalesBudgets]: this.translations.get('lbl_sales_budgets'),
            [DashboardChartType.LeadsBudgets]: this.translations.get('lbl_leads_budgets'),
          };

          return salesLeadsBudgetsSpecsFromDto(data, labels);
        }),
        map$((specs: ChartSpecsWithLegend) => {
          specs.options = {
            ...this.getChartDefaultOptions(),
            ...specs.options,
          };

          if (comboChart) {
            specs.chartType = 'ComboChart';
            (specs.options as any).seriesType = 'line';
            (specs.options as any).series = {
              0: {
                type: 'bars',
              },
              1: {
                type: 'bars',
              },
            };
            specs.legendLayout = [
              chartLegendRow(
                chartLegendCellBar('lbl_sales', '#009ce7'),
                chartLegendCellBar('lbl_leads', '#00ca00'),
                chartLegendCellLineWithDots('lbl_sales_budgets', '#ffa534'),
                chartLegendCellLineWithDots('lbl_leads_budgets', '#ff0000')),
            ];
          }

          return specs;
        }));
  }

  getSalesLeadsBudgetsForSalesperson(): Observable<google.visualization.ChartSpecs> {
    return this.dashboard
      .getSalesLeadsBudgetsForSalesperson().pipe(
        catchError$(this.catchNoPermissions),
        map$((data: any) => {
          const labels = {
            [DashboardChartType.Sales]: this.translations.get('lbl_sales'),
            [DashboardChartType.Leads]: this.translations.get('lbl_leads'),
            [DashboardChartType.SalesBudgets]: this.translations.get('lbl_sales_budgets'),
            [DashboardChartType.LeadsBudgets]: this.translations.get('lbl_leads_budgets'),
          };

          return salesLeadsBudgetsSpecsFromDto(data, labels);
        }),
        map$((specs: google.visualization.ChartSpecs) => {
          specs.options = {
            ...this.getChartDefaultOptions(),
            ...specs.options,
          };

          return specs;
        }));
  }

  getTopCustomersByPriority(): Observable<TopCustomersByPriority[]> {
    return this.dashboard
      .getTopCustomersByPriority().pipe(
        catchError$(this.catchNoPermissions),
        map$((customers: TopCustomersByPriority[]) => customers));
  }

  getTopCustomersByEarliestContact(): Observable<TopCustomersByEarliestContact[]> {
    return this.dashboard
      .getTopCustomersByEarliestContact().pipe(
        catchError$(this.catchNoPermissions),
        map$(topCustomersByEarliestContactFromDto),
        map$((customers: TopCustomersByEarliestContact[]) => customers));
  }

  getWidgets(type: DashboardWidgetsType): Observable<WidgetsModel[]> {
    return this.dashboard.getWidgets(type).pipe(catchError$<any, any>((error) => {
      this.catchNoPermissions(error);
      // if no permissions => return empty list of widgets
      return [[]];
    }));
  }

  updateWidgets(type: DashboardWidgetsType, widgets: WidgetsModel[]): Observable<WidgetsModel[]> {
    return this.dashboard.updateWidgets(type, widgets);
  }

  catchNoPermissions<T>(error: MessageBasket): ObservableInput<T> {
    if (!!error.has && !error.has('msg_denied_no_permissions')) {
      this.notificationService.processAll(error);
    }
    return EMPTY$;
  }

  private addGridTooltip(key: string, tooltip: string): void {
    this.gridTooltips[key] = tooltip;
  }

  private getChartDefaultOptions(): Object {
    return {
      height: 260,
      chartArea: {
        width: '80%',
        height: '70%',
      },
      legend: {
        position: 'top',
        maxLines: 5,
        textStyle: {
          fontSize: 12,
        },
      },
      tooltip: { isHtml: true },
      pointSize: 4,
      hAxis: {
        gridlines: {
          color: 'transparent',
        },
        textStyle: {
          color: '#8b8b8b',
          fontSize: 10,
        },
      },
      vAxis: {
        gridlines: {
          color: 'transparent',
        },
        textStyle: {
          color: '#8b8b8b',
          fontSize: 12,
        },
      },
    };
  }
}
